import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLangContext } from 'components/shared/Lang';
import { useAuthContext } from 'components/shared/Auth';
import { SendOutlined, UserOutlined } from '@ant-design/icons';
import css from '../../ant/css/cards.module.css';
import { Avatar, Col, Row, Drawer } from 'antd';
import classNames from 'classnames';
import styles from '../../ant/css/stylesIndex.module.css';
import { EditorState } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Link from '../../shared/elements/Link';
import ChatMessage from '../../ant/ui/Card/ChatMessage.js';
import CommentForm from '../../ant/ui/Card/CommentForm.js';
import ZotzonAvatar from '../../shared/elements/images/ZotzonAvatar';

export default function CardComment({ item, userData }) {
  const { t } = useLangContext();
  const { data, isAuth } = useAuthContext();
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastComment, setLastComment] = useState(null);
  const [chatRoomUuid, setChatRoomUuid] = useState(item.chat_room_uuid || null);
  const [showChat, setShowChat] = useState(false);
  const messageRef = useRef();
  const pendingUserIdsRef = useRef({});
  const [usersById, setUsersById] = useState({});

  const getUserId = useCallback((user) => {
    if (!user) return null;
    return user?.id ?? user?.user_id ?? user?.uuid ?? user?.user_uuid ?? null;
  }, []);

  const getMessageUserId = useCallback((messageItem) => {
    if (!messageItem) return null;
    return messageItem?.user_id ?? getUserId(messageItem?.user);
  }, [getUserId]);

  const getUserName = useCallback((user) => {
    const normalizedUser = user?.data || user;
    if (!normalizedUser) return '...';
    const fullName = [normalizedUser?.firstName, normalizedUser?.lastName].filter(Boolean).join(' ').trim();
    return fullName || normalizedUser?.name || normalizedUser?.userName || normalizedUser?.username || '...';
  }, []);

  const mergeUsers = useCallback((incomingUsers = []) => {
    const normalizedUsers = Array.isArray(incomingUsers)
      ? incomingUsers
      : incomingUsers && typeof incomingUsers === 'object'
      ? Object.values(incomingUsers)
      : [];

    if (normalizedUsers.length === 0) return;

    setUsersById((prev) => {
      const next = { ...prev };
      normalizedUsers.forEach((incomingUser) => {
        const id = getUserId(incomingUser);
        if (!id) return;
        next[id] = { ...(next[id] || {}), ...incomingUser, id };
      });
      return next;
    });
  }, [getUserId]);

  const onCloseComments = () => {
    setOpen(false);
  };
  const onEditorStateChange = function (editorState) {
    setEditorState(editorState);
  };

  const getEditorMessageText = (state) => {
    if (!state) return '';
    return state.getCurrentContent().getPlainText('\n').trim();
  };

  const extractChatRoomUuid = (listingPayload) => {
    return (
      listingPayload?.chat_room_uuid ||
      listingPayload?.data?.chat_room_uuid ||
      listingPayload?.body?.chat_room_uuid ||
      null
    );
  };

  const refreshListingChatRoomUuid = useCallback(() => {
    return fetch(`/api/listings/${item.id}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((listingPayload) => {
        const uuid = extractChatRoomUuid(listingPayload);
        if (uuid) {
          setChatRoomUuid(uuid);
        }
        return uuid;
      })
      .catch(() => null);
  }, [item.id]);

  const getComments = useCallback((forcedUuid = null) => {
    const resolvedChatRoomUuid = forcedUuid || chatRoomUuid || item.chat_room_uuid || null;
    const url = resolvedChatRoomUuid
      ? `/api/chat-rooms/${resolvedChatRoomUuid}/comments`
      : `/api/comments/${item.id}`;
    fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        mergeUsers(data?.users);
        const normalizedMessages = Array.isArray(data?.data)
          ? data.data.map((messageItem) => ({
              ...messageItem,
              user_id: getMessageUserId(messageItem),
            }))
          : [];

        setMessages(normalizedMessages.slice(0, 3));
        setAllMessages(normalizedMessages);
        messageRef.current?.scroll({
          top: 0,
          behavior: 'smooth',
        });
      })
      .catch((error) => {});
  }, [chatRoomUuid, getMessageUserId, item.chat_room_uuid, item.id, mergeUsers]);

  useEffect(() => {
    setChatRoomUuid(item.chat_room_uuid || null);
  }, [item.chat_room_uuid, item.id]);

  useEffect(() => {
    getComments();
  }, [getComments]);

  useEffect(() => {
    if (!Array.isArray(allMessages) || allMessages.length === 0) return;

    const missingUserIds = [...new Set(allMessages.map((messageItem) => getMessageUserId(messageItem)).filter(Boolean))].filter(
      (id) => !usersById[id] && !pendingUserIdsRef.current[id],
    );

    if (missingUserIds.length === 0) return;

    missingUserIds.forEach((id) => {
      pendingUserIdsRef.current[id] = true;
    });

    Promise.all(
      missingUserIds.map((id) =>
        fetch(`/api/user/${id}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((payload) => ({ id, user: payload }))
          .catch(() => ({ id, user: null })),
      ),
    )
      .then((resolvedUsers) => {
        const nextUsers = [];
        resolvedUsers.forEach(({ user, id }) => {
          if (!user) return;
          if (Array.isArray(user?.data)) {
            nextUsers.push(...user.data);
            return;
          }
          nextUsers.push(user?.data || user);
          if (!getUserId(user?.data || user)) {
            nextUsers.push({ ...(user?.data || user), id });
          }
        });
        mergeUsers(nextUsers);
      })
      .finally(() => {
        missingUserIds.forEach((id) => {
          delete pendingUserIdsRef.current[id];
        });
      });
  }, [allMessages, getMessageUserId, getUserId, mergeUsers, usersById]);

  const putComment = () => {
    const messageText = getEditorMessageText(editorState);
    if (!messageText || isSubmitting) return;

    setIsSubmitting(true);
    const resolvedChatRoomUuid = chatRoomUuid || item.chat_room_uuid || null;
    fetch(`/api/comments`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(resolvedChatRoomUuid ? { chatRoomUUid: resolvedChatRoomUuid } : { listing_id: item.id }),
        parent: null,
        comment: messageText,
      }),
    })
      .then((response) => {
        return response.json();
      })
      .then(async (data) => {
        setLastComment(data?.id);
        setTimeout(() => {
          setLastComment(null);
        }, 2000);
        setEditorState(EditorState.createEmpty());

        let nextChatRoomUuid = resolvedChatRoomUuid;
        if (!nextChatRoomUuid) {
          nextChatRoomUuid = await refreshListingChatRoomUuid();
        }

        return getComments(nextChatRoomUuid);
      })
      .catch(() => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <Row className={css.row}>
        <Col className={classNames(css.box, styles.cardColBox)}>
          <div className={classNames(css.bodyChat, styles.cardBodyChat)}>
            {!isAuth &&
              messages &&
              messages.map((item, index) => {
                if (index < 3 && !item?.t) {
                  return (
                    <ChatMessage
                      key={item?.id ?? `${item?.created_at}_${index}`}
                      messageItem={item}
                      usersById={usersById}
                      getMessageUserId={getMessageUserId}
                      getUserName={getUserName}
                      currentUserId={data?.id}
                      lastComment={lastComment}
                      previewMode
                    />
                  );
                }
              })}
            {isAuth && (
              <div key='messages' ref={messageRef}>
                {messages &&
                  messages.map((item, index) => {
                    return (
                      <ChatMessage
                        key={item?.id ?? `${item?.created_at}_${index}`}
                        messageItem={item}
                        usersById={usersById}
                        getMessageUserId={getMessageUserId}
                        getUserName={getUserName}
                        currentUserId={data?.id}
                        lastComment={lastComment}
                      />
                    );
                  })}

                <div
                  style={{
                    textAlign: 'center',
                    padding: '10px 0',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setOpen(true);
                  }}
                >
                  {allMessages?.length == 0 ? (
                    t('be the first to comment')
                  ) : (
                    <span>
                      {t('show all')} {allMessages?.length} {t(' comments')}
                    </span>
                  )}
                </div>
              </div>
            )}
            <Drawer
              title='Comments'
              placement={'bottom'}
              width={500}
              height={'80%'}
              onClose={onCloseComments}
              open={open}
            >
              <div
                key='messages'
                ref={messageRef}
                style={{
                  maxHeight: '86%',
                  overflowY: 'scroll',
                }}
              >
                {allMessages &&
                  allMessages.map((item, index) => {
                    return (
                      <ChatMessage
                        key={item?.id ?? `${item?.created_at}_${index}`}
                        messageItem={item}
                        usersById={usersById}
                        getMessageUserId={getMessageUserId}
                        getUserName={getUserName}
                        currentUserId={data?.id}
                        lastComment={lastComment}
                      />
                    );
                  })}
              </div>
              {/*{isAuth && !showChat && (*/}
              {/*  <div*/}
              {/*    style={{*/}
              {/*      textAlign: 'right',*/}
              {/*      padding: '0 20px 0 0',*/}
              {/*      cursor: 'pointer',*/}
              {/*    }}*/}
              {/*    onClick={() => setShowChat(true)}*/}
              {/*  >*/}
              {/*    <span*/}
              {/*      style={{*/}
              {/*        color: '#1890ff',*/}
              {/*        textDecoration: 'underline',*/}
              {/*      }}*/}
              {/*    >*/}
              {/*      <Tooltip title={t('add comment')}>*/}
              {/*        <MessageOutlined style={{ fontSize: '22px' }} />*/}
              {/*      </Tooltip>*/}
              {/*    </span>*/}
              {/*  </div>*/}
              {/*)}*/}

              {isAuth && (
                <CommentForm
                  editorState={editorState}
                  onEditorStateChange={onEditorStateChange}
                  onSubmit={putComment}
                  isSubmitting={isSubmitting}
                  isSubmitDisabled={!getEditorMessageText(editorState)}
                  formStyle={{ margin: '4px 4px 20px 4px' }}
                  formItemStyle={{
                    margin: '0px',
                    marginTop: '6px',
                    marginRight: '4px',
                    paddingLeft: '40px',
                  }}
                  leftAdornment={
                    <div style={{ position: 'absolute', bottom: '8px', left: '2px' }}>
                      {userData?.avatarUrl ? (
                        <ZotzonAvatar
                          style={{
                            borderRadius: '50%',
                            height: '30px',
                          }}
                          alt={data?.firstName}
                          src={userData?.avatarUrl}
                        />
                      ) : (
                        <Avatar icon={<UserOutlined />} />
                      )}
                    </div>
                  }
                  editorStyle={{
                    border: '1px solid #ddd',
                    padding: '0px 0px',
                    borderRadius: '0 0 6px 6px',
                    maxHeight: '200px',
                  }}
                  toolbarHidden={true}
                  toolbar={{
                    options: [],
                    inline: { inDropdown: true },
                    list: { inDropdown: true },
                    textAlign: { inDropdown: true },
                    link: { inDropdown: true },
                    history: { inDropdown: true },
                  }}
                  buttonStyle={{
                    position: 'absolute',
                    bottom: '14px',
                    right: '8px',
                    zIndex: '50',
                    width: '26px',
                    height: '26px',
                    borderRadius: '10px',
                    padding: '0 6px 0 6px',
                  }}
                  buttonIcon={<SendOutlined style={{ fontSize: '14px' }} />}
                />
              )}
            </Drawer>
            {!isAuth && !showChat && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '10px 0',
                }}
              >
                <Link href='/auth/sign-in' className={classNames(styles.aLink, styles.aLinkMarginColor)}>
                  {t('log in to comment')}
                </Link>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
}
